import { useCallback } from 'react';
import {
  useMountErrorMessage,
  useOpenOnDesktopAction,
} from '@frameio/shared/resources/mounted-storage';
import { MenuButton, MenuGroup, Tooltip } from '@frameio/vapor';
import { IconCog, IconLink, IconOpenIn } from '@frameio/vapor-icons';
import { useSearchHistoryData } from '../../../shared/contexts';
import { useTrackActionedOnResults } from '../../../shared/contexts/SearchAnalyticsContext';
import { useQuickSearchProjectV2Fragment as useQuickSearchProjectFragment } from '../../../shared/gql/generated/QuickSearchProjectV2';
import { useProjectActions } from '../../../shared/hooks';
import type { SearchMode } from '../../../shared/types';
import { getResultSourceFromSectionId } from '../../../shared/utils';
import { useQuickSearchAppliedFilterTypes } from '../../hooks';
import TEXT from './generated/TEXT/ProjectContextMenuContent';

TEXT.DEFINE({
  /** Menu item label to copy project URL */
  copyUrl: 'Copy URL',
  /** Menu item label to open project settings */
  projectSettings: 'Project Settings',
  /** Menu item label to open project in a new tab */
  openInNewTab: 'Open in New Tab',
});

type ProjectContextMenuContentProps = {
  itemPosition: number;
  projectId: string;
  searchMode: SearchMode;
  searchQuery: string;
};

export const ProjectContextMenuContent = ({
  itemPosition,
  projectId,
  searchMode,
  searchQuery,
}: ProjectContextMenuContentProps) => {
  const trackActionedOnResults = useTrackActionedOnResults();
  const appliedFilterTypes = useQuickSearchAppliedFilterTypes();
  const { data: projectData } = useQuickSearchProjectFragment({
    from: { __typename: 'Project', id: projectId },
  });
  const { addRecentSearch } = useSearchHistoryData();

  const recordSearchHistory = useCallback(() => {
    addRecentSearch({
      query: searchQuery,
      mode: searchMode,
    });
  }, [addRecentSearch, searchQuery, searchMode]);

  const resultSource = getResultSourceFromSectionId(searchMode);
  const {
    handleCopyLink,
    handleOpenProjectSettings,
    handleOpenInNewTab,
    matchType,
  } = useProjectActions({
    appliedFilterTypes,
    projectId,
    resultPosition: itemPosition,
    resultSource,
    searchMode,
    searchQuery,
    workspaceId: projectData?.workspace?.id,
  });
  const {
    icon: openOnDesktopIcon,
    isHidden: isOpenOnDesktopHidden,
    isDisabled: isOpenOnDesktopDisabled,
    label: openOnDesktopLabel,
    onPress: handleOpenOnDesktop,
    error: openOnDesktopError,
  } = useOpenOnDesktopAction({
    projectId,
    trackingSource: 'ContextMenu',
    onPress: () => {
      recordSearchHistory();
      trackActionedOnResults({
        action: 'OpenOnDesktop',
        assetCount: 0,
        filterApplied: appliedFilterTypes,
        folderCount: 0,
        isBulkOperation: false,
        matchType,
        resultPosition: itemPosition,
        resultSource,
        resultType: 'Project',
      });
    },
  });
  const openOnDesktopErrorMessage = useMountErrorMessage(openOnDesktopError);

  return (
    <>
      <MenuGroup>
        <MenuButton iconBefore={<IconOpenIn />} onPress={handleOpenInNewTab}>
          {TEXT.openInNewTab}
        </MenuButton>
        {!isOpenOnDesktopHidden && (
          <Tooltip
            disabled={!isOpenOnDesktopDisabled}
            placement="left"
            title={openOnDesktopErrorMessage}
          >
            <span>
              <MenuButton
                disabled={isOpenOnDesktopDisabled}
                iconBefore={openOnDesktopIcon}
                onPress={handleOpenOnDesktop}
              >
                {openOnDesktopLabel}
              </MenuButton>
            </span>
          </Tooltip>
        )}
      </MenuGroup>
      <MenuGroup>
        <MenuButton iconBefore={<IconLink />} onPress={handleCopyLink}>
          {TEXT.copyUrl}
        </MenuButton>
        <MenuButton
          iconBefore={<IconCog />}
          onPress={handleOpenProjectSettings}
        >
          {TEXT.projectSettings}
        </MenuButton>
      </MenuGroup>
    </>
  );
};
